import {Component, HostListener, isDevMode, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {AuthenticationService} from '../../sdk/service/authentication.service';
import {ROUTES} from '../../app.routes';
import {ASecureForm} from '../../sdk/interface/form/ASecureForm';
import {Keepalive} from '@ng-idle/keepalive';
import {DEFAULT_INTERRUPTSOURCES, Idle} from '@ng-idle/core';
import {AlertService} from '../../sdk/service/alert.service';
import {EMPTY, Subject, takeUntil} from 'rxjs';
import {catchError, switchMap} from 'rxjs/operators';
import {
    BusinessDetails,
    PrepaidServicesStatusInfo,
    UserBusinessListItem,
    UserLogin,
    WebUserService
} from 'web-angular-client';
import {LayoutService} from './layout.service';
import {ConfirmationService} from '../../sdk/service/confirmation.service';
import {Menu} from 'primeng/menu';
import {EnvironmentService} from '../../sdk/service/environment.service';
import {environment} from '../../../environments/environment';
import {FilterPersistenceService} from '../../sdk/component/search/filter-persistence.service';
import {MenuContext, MenuExternal, MenuItem, resolveSideMenu} from './menu-config';
import {assertMaxDepth, buildMenu} from './menu-config/menu-builder';
import {IEnvironment} from '../../../environments/ienvironment';
import {EcommerceVerificationService} from '../../sdk/service/ecommerce-verification.service';
import {EcommerceVerificationState} from '../../sdk/interface/ecommerce-verification-state';
import {CONTENT_HEADER_LOGOS, ContentHeaderLogo, ContentHeaderLogoKey} from './content-header-logo';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';

@Component({
    selector: 'app-menu',
    templateUrl: './layout.component.html',
    styleUrls: ['./layout.component.scss'],
    standalone: false
})
export class LayoutComponent extends ASecureForm implements OnInit, OnDestroy
{
    @ViewChild('menu') menu!: Menu;
    isMediumSmallDisplay: boolean = false;
    isSidebarClosed: boolean = false;
    items: MenuItem[] = [];
    openGroupKey: string | null = null;
    businessList: UserBusinessItem[];
    selectedBusinessId: number;
    accountSettingsHidden = false;
    showEcommerceLanding = false;
    pageHeaderText: string;
    pageHeaderTooltipText: string;
    pageHeaderCentered: boolean;
    contentHeaderLogo: ContentHeaderLogo | null = null;
    protected businessDetails: BusinessDetails = null;
    private merchantPrefix: string;
    private userName: string;
    private merchantId: number;
    private logoImage: string;
    private prepaidServicesStatusInfo: PrepaidServicesStatusInfo = null;
    private readonly menuConfig: MenuItem[];
    private prepaidStatusInterval: ReturnType<typeof setInterval> | null = null;
    private destroy$ = new Subject<void>();

    constructor(
        private idle: Idle,
        private keepalive: Keepalive,
        private router: Router,
        private alertService: AlertService,
        private filterPersistenceService: FilterPersistenceService,
        protected authenticationService: AuthenticationService,
        protected environmentService: EnvironmentService,
        protected layoutService: LayoutService,
        protected userService: WebUserService,
        private ecommerceVerificationService: EcommerceVerificationService,
        private confirmationService: ConfirmationService,
        private activatedRoute: ActivatedRoute,
        private modalService: NgbModal)
    {
        super(authenticationService, environmentService);

        this.menuConfig = resolveSideMenu(environment);
        if (isDevMode())
        {
            assertMaxDepth(this.menuConfig);
        }
        this.rebuildMenu();

        router.events.pipe(takeUntil(this.destroy$)).subscribe((val) =>
        {
            if (val instanceof NavigationEnd)
            {
                this.setupPageHeaderFromSettings();
            }
        });

        // sets an idle timeout of x seconds.
        idle.setIdle(55);
        // sets a timeout period of x seconds. after x seconds of inactivity, the user will be
        // considered timed out.
        idle.setTimeout(2022);
        // sets the default interrupts, in this case, things like clicks, scrolls, touches to the document
        idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        //  idle.onIdleEnd.subscribe(() => this.idleState = 'No longer idle.');
        idle.onTimeout.pipe(takeUntil(this.destroy$)).subscribe(() =>
        {
            this.logout();
        });
        // idle.onIdleStart.subscribe(() => this.idleState = 'You\'ve gone idle!');
        // idle.onTimeoutWarning.subscribe((countdown) => this.idleState = 'You will time out in ' + countdown + '
        // seconds!');

        this.keepalive.interval(45);

        this.keepalive.onPing.pipe(
            takeUntil(this.destroy$),
            switchMap(() => this.authenticationService.ping().pipe(
                catchError((error) =>
                {
                    this.alertService.error(error, true);
                    this.logout();
                    return EMPTY;
                })
            ))
        ).subscribe();

        this.idle.watch();

        const userLogin = this.authenticationService.getUserLogin();

        if (!userLogin)
        {
            this.logout();
            return;
        }
        this.merchantId = userLogin.businessId;

        // subscribe to the layout service to automatically update the display name and logo
        // every time an account update happens.
        this.layoutService.load.pipe(takeUntil(this.destroy$)).subscribe(
            businessDetails =>
            {
                this.businessDetails = businessDetails;
                this.merchantPrefix = this.businessDetails.merchantPrefix;
                if (this.businessDetails.logo)
                {
                    this.logoImage = 'data:image/png;base64,' + this.businessDetails.logo;
                }
                this.rebuildMenu();
            });

        this.layoutService.businessList.pipe(takeUntil(this.destroy$)).subscribe({
            next: (businessList: UserBusinessListItem[]) =>
            {
                this.businessList = businessList.map(b =>
                {
                    return {
                        ...b,
                        disabled: !b.userEnabled
                    };
                });

                this.selectedBusinessId = this.businessList.find(b => b.businessId === this.merchantId).businessId;
            }
        });


        this.debugOutput('Layout UserName : ' + this.authenticationService.getUserLogin().username);
        this.userName = this.authenticationService.getUserLogin().username;

        this.layoutService.prepaidServicesStatusInfo.pipe(takeUntil(this.destroy$)).subscribe((prepaidServicesStatusInfo: PrepaidServicesStatusInfo) =>
        {
            this.prepaidServicesStatusInfo = prepaidServicesStatusInfo;
        });
    }

    get showSideBar(): boolean
    {
        if (!this.isMediumSmallDisplay)
        {
            return false;
        }
        else if (this.isMediumSmallDisplay && !this.isSidebarClosed)
        {
            return true;
        }
    }

    get isSignUpFeePaid(): boolean
    {
        return this.prepaidServicesStatusInfo ? this.prepaidServicesStatusInfo.signupFeePaid : true;
    }

    get hideSubscriptionBanner(): boolean
    {
        return this.prepaidServicesStatusInfo ? this.prepaidServicesStatusInfo.subscriptionFeePaid : true;
    }

    get isGracePeriodExpired(): boolean
    {
        return this.prepaidServicesStatusInfo ? !this.prepaidServicesStatusInfo.gracePeriodValid : false;
    }

    get getMerchantPrefix(): string
    {
        return this.merchantPrefix;
    }

    get getExternalIdentifier(): string
    {
        return this.businessDetails?.externalIdentifier;
    }

    get getInstanceLogoPaddingClass(): string
    {
        return 'sidebar-header ' + this.environmentService.inAppInstanceLogoPaddingClass;
    }

    get logoMain(): string
    {
        return this.environmentService.logoMain;
    }

    get logoSecondary(): string
    {
        return this.environmentService.logoSecondary;
    }

    get contentHeaderLogoWidth(): number
    {
        return this.environmentService.contentHeaderLogoWidth;
    }

    isEcommerceMerchant(): boolean
    {
        return this.businessDetails?.eCommerceMerchant === true;
    }

    ngOnDestroy()
    {
        this.destroy$.next();
        this.destroy$.complete();
        this.idle.stop();
        this.keepalive.stop();
        if (this.prepaidStatusInterval)
        {
            clearInterval(this.prepaidStatusInterval);
            this.prepaidStatusInterval = null;
        }
    }

    ngOnInit()
    {
        this.isMediumSmallDisplay = window.innerWidth < 1440;
        this.updateSidebarStatus();
        this.loadEcommerceMenuVisibility();
        this.layoutService.loadBusinessListForUser();
        this.layoutService.loadBusinessDetails();
        this.layoutService.loadPrepaidServicesStatusInfo();

        this.prepaidStatusInterval = setInterval(() =>
        {
            this.layoutService.loadPrepaidServicesStatusInfo();
        }, 10000);

        // For scenarios where :has() is unsupported, combine CSS with JavaScript-based fallback to toggle the class
        // manually
        if (!CSS.supports('selector(:has())'))
        {
            const dropdowns = document.querySelectorAll('.dropdown-toggle');
            dropdowns.forEach(toggle =>
            {
                const submenu = toggle.nextElementSibling;
                if (submenu?.querySelector('.active-link'))
                {
                    toggle.classList.add('active-link');
                }
            });
        }
    }

    @HostListener('window:resize')
    onResize()
    {
        this.isMediumSmallDisplay = window.innerWidth < 1440;
        this.updateSidebarStatus();
    }

    public updateSidebarStatus()
    {
        if (this.isMediumSmallDisplay && !this.isSidebarClosed)
        {
            this.closeSidebar();
        }
        else if (!this.isMediumSmallDisplay && this.isSidebarClosed)
        {
            this.openSidebar();
        }
    }

    clearFilters()
    {
        this.filterPersistenceService.clearAllFilters();
    }

    logout()
    {
        this.idle.stop();
        this.keepalive.stop();
        if (this.prepaidStatusInterval)
        {
            clearInterval(this.prepaidStatusInterval);
            this.prepaidStatusInterval = null;
        }
        this.authenticationService.logout().subscribe();
        this.clearFilters();
        this.modalService.dismissAll();
        this.router.navigate([ROUTES.PUBLIC.LOGIN.path]).then();
    }

    public toggleSidebar()
    {
        this.isSidebarClosed ? this.openSidebar() : this.closeSidebar();
    }

    public openSidebar()
    {
        this.isSidebarClosed = false;
    }

    public closeSidebar()
    {
        this.isSidebarClosed = true;
    }

    public getUserName()
    {
        return this.userName;
    }

    public getBulkEnabled()
    {
        if ((super.hasRouteAccess(this.routes.SECURE.ACCOUNT.UPLOAD_REQUESTS_FILE) ||
            super.hasRouteAccess(this.routes.SECURE.ACCOUNT.READONLY_UPLOAD_REQUESTS_FILE)))
        {
            return this.businessDetails?.bulkEnabled;
        }
        else
        {
            return false;
        }
    }

    public getQrCodesEnabled()
    {
        if (super.hasRouteAccess(this.routes.SECURE.QR.LIST))
        {
            return this.businessDetails?.qrCodesEnabled;
        }
        else
        {
            return false;
        }
    }

    public getLogoImage()
    {
        return this.logoImage;
    }

    toggleGroup(collapseKey: string)
    {
        this.openGroupKey = this.openGroupKey === collapseKey ? null : collapseKey;
    }

    onExternal(item: MenuExternal)
    {
        if (item.onClick === 'logout')
        {
            this.logout();
            return;
        }
        if (item.onClick === 'help')
        {
            this.onGetHelpClick();
        }
    }

    rebuildMenu()
    {
        const ctx: MenuContext = {
            isEcommerceMerchant: this.isEcommerceMerchant(),
            isBulkEnabled: !!this.getBulkEnabled(),
            isQrCodesEnabled: !!this.getQrCodesEnabled(),
            isAccountSettingsHidden: this.accountSettingsHidden,
            showEcommerceLanding: this.showEcommerceLanding,
            env: environment as IEnvironment
        };
        this.items = buildMenu(this.menuConfig, ctx, p => this.hasPrivilege(p));
    }

    // Switches the user's current business profile based on their selection from the businessList dropdown.
    switchMerchant()
    {
        const selectedItem: UserBusinessItem = this.businessList.find(b => b.businessId === this.selectedBusinessId);
        this.confirmationService.confirm(
            'Switch Business Profile',
            `Are you sure you wish to switch to "${selectedItem.displayName}" profile?`)
            .then((confirmed) =>
            {
                if (confirmed)
                {
                    this.userService.switchMerchantProfile(selectedItem.businessId).subscribe({
                        next: (userLogin: UserLogin) =>
                        {
                            this.authenticationService.setUserLogin(userLogin);
                            this.router.navigateByUrl('/', {skipLocationChange: true}).then(() =>
                            {
                                this.router.navigate([ROUTES.SECURE.HOME.VIEW.path]);
                            });
                        },
                        error: (error) =>
                        {
                            this.alertService.error(error, true);
                            this.selectedBusinessId = this.merchantId;
                        }
                    });
                }
            });
    }

    onGetHelpClick()
    {
        this.confirmationService.confirm(
            'Heads up!',
            environment.getHelpModalText, 'OK',
            'Cancel',
            'md',).then(
            (confirmed) =>
            {
                if (confirmed)
                {
                    window.open(environment.supportPortalUrl, '_blank');
                }
            });
    };

    /**
     * Resolves the verification-driven menu visibility for guest-admin e-commerce merchants: the
     * Account Settings item stays hidden while their KYC is unresolved (pending or not started) and
     * their AVS has not failed, and the Home item linking back to the e-commerce landing page shows
     * only while they should still see that page. A failed or verified KYC, or a failed AVS, keeps
     * Account Settings visible so the merchant can fix, resubmit or manage their details. Non
     * e-commerce merchants and non-guest-admins are unaffected.
     */
    private loadEcommerceMenuVisibility(): void
    {
        const userLogin = this.authenticationService.getUserLogin();
        if (!this.environmentService.eCommerceEnabled
            || userLogin?.eCommerceMerchant !== true
            || !this.isGuestAdminUser())
        {
            return;
        }

        // Fail closed until the verification state resolves: hide Account Settings and restrict the menu to the
        // e-commerce landing experience, so the full app menu is not briefly exposed while the state is loading.
        this.accountSettingsHidden = true;
        this.showEcommerceLanding = true;
        this.rebuildMenu();
        this.ecommerceVerificationService.getVerificationState().pipe(takeUntil(this.destroy$)).subscribe({
            next: (verificationState: EcommerceVerificationState) =>
            {
                const isGuestAdmin = this.isGuestAdminUser();
                this.accountSettingsHidden =
                    this.ecommerceVerificationService.isAccountSettingsHidden(verificationState, isGuestAdmin);
                this.showEcommerceLanding =
                    this.ecommerceVerificationService.shouldDisplayLandingPage(verificationState, isGuestAdmin);
                this.rebuildMenu();
            },
            error: (error) =>
            {
                this.debugOutput('Failed to load verification state for e-commerce menu visibility', error);
                // Surface the Home link so a guest-admin whose verification state failed to load is not
                // left with Account Settings hidden and no way back to the e-commerce landing page.
                this.showEcommerceLanding = true;
                this.rebuildMenu();
            }
        });
    }

    private setupPageHeaderFromSettings()
    {
        const pageHeaderSettings = this.activatedRoute.snapshot.firstChild.data?.pageHeaderSettings ?? null;
        this.pageHeaderText = pageHeaderSettings?.pageHeaderText ?? ' Page Header';
        this.pageHeaderTooltipText = pageHeaderSettings?.pageHeaderTooltipText ?? null;
        this.pageHeaderCentered = pageHeaderSettings?.pageHeaderCentered ?? false;
        const logoKey: ContentHeaderLogoKey | undefined = pageHeaderSettings?.contentHeaderLogo;
        this.contentHeaderLogo = logoKey ? CONTENT_HEADER_LOGOS[logoKey] ?? null : null;
    }
}

interface UserBusinessItem
{
    businessId?: number;
    displayName?: string;
    userEnabled?: boolean;
    disabled: boolean;
}
